<script lang="ts">
  import Dialog, { Content } from '@smui/dialog';
  import { isDirectionRTL } from '../../../../common/util';
  import Text from '../../../common/components/Text.svelte';
  import { addProductToCart } from '../../services/pdp-data-services';
  import {
    pdpSellerInfoData,
    productDetailMainData,
    productQuantity,
    selectedGiftGroup,
    selectedGifts,
    sellerOfferId,
    showOtherSellerModal,
  } from '../../store/pdp_store.js';
  import OtherSellerCurrentOfferCard from './OtherSellerCurrentOfferCard.svelte';
  import OtherSellerOfferCard from './OtherSellerOfferCard.svelte';

  const isRTL = isDirectionRTL();
  const mpOfferData = $pdpSellerInfoData?.marketPlaceOfferData;
  const earliestDeliveryDays = mpOfferData?.earliestDeliveryDays;
  const latestDeliveryDays = mpOfferData?.latestDeliveryDays;
  const marketPlaceOffersByCondition = $pdpSellerInfoData?.marketPlaceOffersByCondition ?? [];
  let hoveredTooltip: string | null = null;

  const TAB_CONFIG = {
    NEW: {
      key: 'new',
      labelKey: 'extra.marketplace.pdp.seller.tab.label.new',
      tooltipLabelKey: '',
      image: '',
    },
    OPEN_BOX: {
      key: 'open',
      labelKey: 'extra.marketplace.pdp.seller.tab.label.open.box',
      tooltipLabelKey: 'extra.marketplace.pdp.seller.openbox.tooltip.label',
      image: '/_ui/responsive/theme-extra/images/openbox.svg',
    },
    RENEWED: {
      key: 'renewed',
      labelKey: 'extra.marketplace.pdp.seller.tab.label.renewed',
      tooltipLabelKey: 'extra.marketplace.pdp.seller.renew.tooltip.label',
      image: '/_ui/responsive/theme-extra/images/renewed.svg',
    },
  };

  let activeTab = 'new';
  $: if ($showOtherSellerModal) {
    const newTab = marketPlaceOffersByCondition.find(({ conditionCode }) => conditionCode === 'NEW');

    activeTab = newTab
      ? TAB_CONFIG.NEW.key
      : (TAB_CONFIG[marketPlaceOffersByCondition?.[0]?.conditionCode]?.key ?? 'new');
  }
  $: activeGroup =
    marketPlaceOffersByCondition?.find(group => TAB_CONFIG[group.conditionCode]?.key === activeTab) ?? null;

  function closeHandler() {
    $showOtherSellerModal = false;
  }

  function handleOfferAddToCart(offerId, quantity) {
    sellerOfferId.set(offerId);
    productQuantity.updateQuantity(quantity);

    addProductToCart(
      $productDetailMainData?.isTransactional,
      $productDetailMainData?.hasFreeGift,
      $productDetailMainData?.freeGiftDataList,
      $selectedGifts,
      $selectedGiftGroup,
      $productDetailMainData?.isRSAItem
    );
  }

  function onTooltipHover(offerId) {
    hoveredTooltip = offerId;
  }

  function onTooltipLeave() {
    hoveredTooltip = null;
  }
</script>

<section class="other-seller-modal-wrapper">
  <Dialog
    bind:open={$showOtherSellerModal}
    on:SMUIDialog:closed={closeHandler}
    class="extraDrawer-container other-seller-dialog {isRTL ? 'rtl' : ''}"
  >
    <Content>
      <div class="other-seller-modal-container">
        <div class="product-display-header">
          <button
            type="button"
            on:click={closeHandler}
            aria-label="Close"
            class="c_icon_new c_icon_new--close close-btn"
          />
        </div>
        {#if mpOfferData}
          <OtherSellerCurrentOfferCard
            {mpOfferData}
            productName={$productDetailMainData?.name}
            productImageUrl={$productDetailMainData?.amplienceProductBaseUrl}
            {earliestDeliveryDays}
            {latestDeliveryDays}
            onAddToCart={handleOfferAddToCart}
          />
        {/if}

        <div class="tabs-section">
          <div class="tabs-header">
            {#each marketPlaceOffersByCondition as group, i}
              {#if TAB_CONFIG[group.conditionCode]}
                <button
                  class="tab-button {activeTab === TAB_CONFIG[group.conditionCode].key ? 'active' : ''}"
                  on:click={() => (activeTab = TAB_CONFIG[group.conditionCode].key)}
                >
                  <Text key={TAB_CONFIG[group.conditionCode].labelKey} args={[group.offers.length]} />
                </button>

                {#if i < marketPlaceOffersByCondition.length - 1}
                  <div class="separator-vertical"></div>
                {/if}
              {/if}
            {/each}
          </div>

          <div class="tab-content">
            <div class="tab-pane active">
              {#each activeGroup?.offers ?? [] as offer (offer.id)}
                <OtherSellerOfferCard
                  {offer}
                  {activeGroup}
                  tabConfig={TAB_CONFIG}
                  {hoveredTooltip}
                  onAddToCart={handleOfferAddToCart}
                  {onTooltipHover}
                  {onTooltipLeave}
                  giftItem={$productDetailMainData?.freeGiftDataList}
                />
              {/each}
            </div>
          </div>
        </div>
      </div>
    </Content>
  </Dialog>
</section>

<style lang="scss">
  @import '../../../common/styles/colors.new.scss';

  :global {
    html:has(.other-seller-modal-wrapper .mdc-dialog--open) {
      overflow: hidden !important;
    }
  }

  .other-seller-modal-wrapper {
    :global(.extraDrawer-container.other-seller-dialog) {
      height: 100%;
      z-index: 999 !important;
      :global(.mdc-dialog__container) {
        :global(.mdc-dialog__content) {
          padding: 19px 21px;
          overflow-y: auto;
          scrollbar-width: none;
          &::-webkit-scrollbar {
            display: none;
          }
        }
        :global(.mdc-dialog__surface) {
          max-width: 620px;
          min-height: 100%;
          min-width: unset;
          @include desktop-max-width {
            max-width: 100vw;
          }
        }
      }
    }
    .product-display-header {
      display: flex;
      justify-content: flex-end;
      background: transparent;

      .close-btn {
        cursor: pointer;
        font-size: 24px;
        color: $neutral-dark-02;
        border: none;
        background: transparent;
        padding: 0;
        outline: none;
      }
    }
    .separator-horizontal {
      width: 298px;
      height: 1px;
      background-color: $secondary-gray-02;
      margin-top: 12px;
      @include desktop-max-width {
        margin-top: 0px;
      }
    }

    .separator-vertical {
      width: 1px;
      height: 25px;
      background-color: $secondary-gray-02;
      flex-shrink: 0;
      border-radius: 1px;
      display: block;
    }

    .other-seller-modal-container {
      display: flex;
      flex-direction: column;
      gap: 24px;
      padding: 19px 21px;
      @include desktop-max-width {
        padding: 16px;
      }
    }

    .product-seller-info {
      display: flex;
      flex-direction: column;
      gap: 20px;
      background-color: $neutral-light-04;
      border: 1px solid $primary-blue;
      border-radius: 12px;
      padding: 20px 16px;
      @include desktop-max-width {
        padding: 16px;
      }
    }

    .tabs-section {
      display: flex;
      flex-direction: column;
      gap: 24px;
      .separator-vertical {
        height: 14px;
      }
      .tabs-header {
        display: flex;
        flex-wrap: wrap;
        gap: 8px 12px;
        justify-content: center;
        align-items: center;
        padding: 14px;
        background-color: $neutral-light-04;
        @include desktop-max-width {
          gap: 8px 10px;
          padding: 12px 10px;
        }

        .tab-button {
          background: transparent;
          border: none;
          border-radius: 0;
          min-width: 83px;
          padding: 0px;
          font-size: 14px;
          font-weight: 500;
          color: $neutral-dark-02;
          position: relative;
          border-bottom: 2px solid transparent;
          flex-shrink: 0;

          &.active {
            font-weight: 700;
            color: $primary-blue;
            border-bottom-color: $primary-blue;
          }
        }
      }

      .tab-content {
        display: grid;
        gap: 24px;
      }
    }
  }
</style>
